import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { ArrowLeft, Save, Upload, Link as LinkIcon, Type, FileText, PlayCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../api';

interface Module {
  _id: string;
  title: string;
  description?: string;
  type: 'video' | 'pdf' | 'text' | 'link' | 'document';
  content?: string; // For text content or URL for video/pdf/link
  fileUrl?: string; // For uploaded files
  externalUrl?: string; // For external links
  duration?: number;
  order?: number;
  isRequired?: boolean;
  section: string;
  quiz?: string;
}

const CourseViewerEdit: React.FC = () => {
  const { courseId, sectionId, moduleId } = useParams<{ courseId: string; sectionId: string; moduleId: string }>();
  const navigate = useNavigate();

  const [module, setModule] = useState<Module | null>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<Partial<Module>>({});
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    const fetchModule = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/professor/modules/${moduleId}`);
        if (response.data && response.data.module) {
          const fetchedModule = response.data.module;
          setModule(fetchedModule);
          setFormData({
            title: fetchedModule.title,
            description: fetchedModule.description,
            type: fetchedModule.type,
            content: fetchedModule.type === 'text' ? fetchedModule.content : (fetchedModule.fileUrl || fetchedModule.externalUrl),
            duration: fetchedModule.duration,
            order: fetchedModule.order,
            isRequired: fetchedModule.isRequired,
            section: fetchedModule.section,
            quiz: fetchedModule.quiz,
          });
        } else {
          toast.error('Module non trouvé');
          navigate(`/professor/courses/${courseId}`);
        }
      } catch (error) {
        console.error('Error fetching module for edit:', error);
        toast.error('Erreur lors du chargement du module pour édition');
        navigate(`/professor/courses/${courseId}`);
      } finally {
        setLoading(false);
      }
    };

    fetchModule();
  }, [moduleId, courseId, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!module) return;

    const submitData = new FormData();
    for (const key in formData) {
      if (formData[key as keyof Partial<Module>] !== undefined) {
        submitData.append(key, String(formData[key as keyof Partial<Module>]));
      }
    }
    if (file) {
      submitData.append('file', file);
    }

    try {
      const response = await api.put(`/professor/modules/${moduleId}`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data) {
        toast.success('Module mis à jour avec succès');
        navigate(`/professor/courses/${courseId}`);
      } else {
        toast.error('Erreur lors de la mise à jour du module');
      }
    } catch (error) {
      console.error('Error updating module:', error);
      toast.error('Erreur lors de la mise à jour du module');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
        </div>
      </Layout>
    );
  }

  if (!module) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Module non trouvé</h3>
          <button
            onClick={() => navigate(`/professor/courses/${courseId}`)}
            className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            Retour au cours
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => navigate(`/professor/courses/${courseId}`)}
              className="mr-4 text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Modifier le module: {module.title}</h1>
          </div>
          <button
            onClick={handleSubmit}
            className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            <Save className="h-4 w-4 mr-2" />
            Enregistrer
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">Titre du module</label>
              <input
                type="text"
                name="title"
                id="title"
                value={formData.title || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                id="description"
                value={formData.description || ''}
                onChange={handleChange}
                rows={3}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
              ></textarea>
            </div>

            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700">Type de contenu</label>
              <select
                name="type"
                id="type"
                value={formData.type || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                required
              >
                <option value="">Sélectionner un type</option>
                <option value="video">Vidéo</option>
                <option value="pdf">PDF</option>
                <option value="text">Texte</option>
                <option value="link">Lien externe</option>
                <option value="document">Document (Word, Excel, etc.)</option>
              </select>
            </div>

            {/* Conditional Content Input */}
            {formData.type === 'text' && (
              <div>
                <label htmlFor="content" className="block text-sm font-medium text-gray-700">Contenu textuel</label>
                <textarea
                  name="content"
                  id="content"
                  value={formData.content || ''}
                  onChange={handleChange}
                  rows={10}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                  required
                ></textarea>
              </div>
            )}

            {(formData.type === 'video' || formData.type === 'pdf' || formData.type === 'document') && (
              <div>
                <label htmlFor="file" className="block text-sm font-medium text-gray-700">Fichier</label>
                <input
                  type="file"
                  name="file"
                  id="file"
                  onChange={handleFileChange}
                  className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
                />
                {module?.fileUrl && (
                  <p className="mt-2 text-sm text-gray-500">
                    Fichier actuel: <a href={module.fileUrl} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">{module.fileUrl.split('/').pop()}</a>
                  </p>
                )}
                <input
                  type="text"
                  name="content"
                  id="content"
                  placeholder="Ou entrez une URL de fichier existant (ex: YouTube, Vimeo, URL directe)"
                  value={formData.content || ''}
                  onChange={handleChange}
                  className="mt-2 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            )}

            {formData.type === 'link' && (
              <div>
                <label htmlFor="content" className="block text-sm font-medium text-gray-700">URL du lien</label>
                <input
                  type="url"
                  name="content"
                  id="content"
                  value={formData.content || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                  placeholder="https://example.com"
                  required
                />
              </div>
            )}

            <div>
              <label htmlFor="duration" className="block text-sm font-medium text-gray-700">Durée (minutes)</label>
              <input
                type="number"
                name="duration"
                id="duration"
                value={formData.duration || 0}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="isRequired"
                id="isRequired"
                checked={formData.isRequired || false}
                onChange={handleChange}
                className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
              />
              <label htmlFor="isRequired" className="ml-2 block text-sm text-gray-900">Module requis</label>
            </div>

            {/* Add fields for order, section, quiz if needed */}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate(`/professor/courses/${courseId}`)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
              >
                <Save className="h-4 w-4 mr-2" />
                Enregistrer les modifications
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default CourseViewerEdit;
